import { View, SxProp, useDripsyTheme, SafeAreaView, ScrollView } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import React, { useContext, useEffect, useState } from 'react';
import { H3, H5, TextBody, TextCaption } from '@/design-system/components/typography';
import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { TabKeys } from '../../options';
import { AccountDetails } from './Details.account';
import { CardDetails } from './Details.card';
import { AccountDetails as AccountDetailsNative } from './DetailsNative.account';
import { CardDetails as CardDetailsNative } from './DetailsNative.card';
import { LayoutContext } from 'contexts/LayoutContext';
import { PageContainer } from '../page/PageContainer';

import { AccountDetailsTabParamList, AccountDetailsTabRoutes, AccountDetailsTabScreenProps } from './type';
import { CardAccountType, CardAccountTypeColor } from '@/features/card-account/enum';

export interface AccountDetailsScreenProps {
  account: string;
  tab?: number;
  record: any;
  flag?: string;
}

export const AccountDetailsAccountScreen: React.FC<AccountDetailsTabScreenProps<AccountDetailsTabRoutes.Account>> = ({ route }) => {
  const navigation = useNavigation();
  const { t } = useTranslation('account-management');
  const { theme } = useDripsyTheme();
  const { account, tab, record, flag } = route.params;
  const layout = useContext(LayoutContext);
  const [tabIndex, setTabIndex] = useState(tab || 0);

  const onBackPressed = () => {
    navigation.getParent()?.goBack();
  };

  return layout.isMobile ? (
    <>
      <SafeAreaView sx={styles.containerSaft}>
        <ScrollView sx={styles.scrollContent} nestedScrollEnabled>
          <View sx={{ ...styles.container, margin: 0 }}>
            {tabIndex === 0 ? <AccountDetailsNative account={account} tab={tab} record={record} /> : null}
            {tabIndex === 1 && record?.cardAccountId ? <CardDetailsNative account={account} tab={tab} record={record} flag={flag} /> : null}
          </View>
        </ScrollView>
      </SafeAreaView>
    </>
  ) : (
    <ScrollView sx={styles.containerWeb} nestedScrollEnabled>
      <PageContainer sx={{ paddingX: '$10', paddingY: '$10' }} childrenSx={{ zIndex: 1 }}>
        <View sx={{ ...styles.container }}>
          <View sx={styles.back}>
            <UniIconButton
              sx={styles.backButton}
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              icon="arrowLeft"
              onPress={onBackPressed}
            />
            <TextCaption hyperlink>
              {flag == '1'
                ? t('card-account-management::account-page.text--back-to-card-account')
                : t('card-account-management::account-page.text--back-to-account-management')}
            </TextCaption>
          </View>
          <View sx={styles.header}>
            <Icon name="accountProfile" width={60} height={60} />
            <View sx={styles.account}>
              <H3 bold>{(flag == '1' ? record?.label : record?.name) || '-'}</H3>
              <TextBody sx={{ mt: '$1' }}>
                {(flag == '1' ? (
                  <View sx={{ gap: '$2', flexDirection: 'row' }}>
                    <View
                      sx={{
                        ...styles.tagSx,
                        backgroundColor: theme.colors?.$tagBlackBackground as string
                      }}>
                      <TextCaption sx={{ color: theme.colors?.$tagBlackText as string }}>{record.assetSymbol}</TextCaption>
                    </View>
                    <View
                      sx={{
                        ...styles.tagSx,
                        backgroundColor: theme.colors[`$tag${CardAccountTypeColor[record.type]}Background`] as string
                      }}>
                      <TextCaption sx={{ color: theme.colors[`$tag${CardAccountTypeColor[record.type]}Text`] as string }}>
                        {CardAccountType[record.type]}
                      </TextCaption>
                    </View>
                  </View>
                ) : (
                  record?.accountNumber
                )) || '-'}
              </TextBody>
            </View>
          </View>
          {flag != '1' && record?.cardAccountId && (
            <View sx={styles.operations}>
              <SegmentSelect
                data={TabKeys}
                value={tabIndex}
                wrapperBackgroundColor={theme?.colors?.$backgroundPrimary as string}
                buttonWidth={188}
                buttonBackgroundColors={theme?.linearGradients?.SegmentSelectActive}
                labelColorActive={theme?.colors?.$white as string}
                labelColorInactive={theme?.colors?.$navbarTextInactive as string}
                onValueChange={setTabIndex}
              />
            </View>
          )}
          <View sx={styles.contentContainer}>
            {tabIndex === 0 ? <AccountDetails account={record?.id} tab={tab} record={record} /> : null}
            {tabIndex === 1 && record?.cardAccountId ? (
              <CardDetails account={record?.cardAccountId} tab={tab} record={record} flag={flag} />
            ) : null}
          </View>
        </View>
      </PageContainer>
    </ScrollView>
  );
};

enum StyleName {
  containerSaft = 'containerSaft',
  scrollContent = 'scrollContent',
  container = 'container',
  back = 'back',
  backButton = 'backButton',
  header = 'header',
  account = 'account',
  operations = 'operations',
  contentContainer = 'contentContainer',
  containerWeb = 'containerWeb',
  tagSx = 'tagSx'
}

const styles: Record<StyleName, SxProp> = {
  containerSaft: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary'
  },
  scrollContent: {
    flex: 1,
    height: '100%',
    backgroundColor: '$backgroundSecondary'
  },
  container: {
    // margin: '$10',
    flexDirection: 'column',
    height: '100%'
  },
  back: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '$2'
  },
  backButton: {
    backgroundColor: '$hyperlink',
    borderRadius: 5,
    width: 20
  },
  header: {
    mt: '$5',
    flexDirection: 'row',
    alignItems: 'center'
  },
  account: {
    ml: '$5'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    mt: '$7',
    alignItems: 'center',
    height: 44
  },
  contentContainer: {
    mt: '$7'
  },
  containerWeb: {
    flex: 1,
    backgroundColor: 'transparent'
  },
  tagSx: { paddingX: '$2', paddingY: '$1', width: 'fit-content', borderRadius: 100 }
};
